<template>
  <base-content>
    <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin tw-border-b tw-pb-2">Activate Plan</h1>
    <div class="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-4" v-if="isLoading">
      <base-card class="tw-divide-y tw-divide-gray-200 tw-font-light tw-text-sm">
        <div class="tw-flex tw-justify-between tw-py-3" v-for="i in 7" :key="i">
          <base-skeleton-loader class="tw-h-3 tw-w-12" />
          <base-skeleton-loader class="tw-h-3 tw-w-12" />
        </div>
      </base-card>
    </div>
    <div class="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-4" v-else>
      <base-card class="tw-divide-y tw-divide-gray-200 tw-font-light tw-text-sm">
        <div class="tw-flex tw-justify-between tw-py-2">
          <div class="tw-text-gray-500">Plan</div>
          <div>{{ plan.planName }}</div>
        </div>
        <div class="tw-flex tw-justify-between tw-py-2">
          <div class="tw-text-gray-500">Business</div>
          <div>{{ plan.businessName }}</div>
        </div>
        <div class="tw-flex tw-justify-between tw-py-2">
          <div class="tw-text-gray-500">Start Date</div>
          <div>{{ formatDate(plan.startDate) }}</div>
        </div>
        <div class="tw-flex tw-justify-between tw-py-2">
          <div class="tw-text-gray-500">Beginning Credits</div>
          <div class="tw-space-x-4">
            <span v-for="item in plan.itemCategories" :key="item.businessItemCategoryId">{{ item.quantity + item.initialQuantity }}</span>
          </div>
        </div>
        <div class="tw-flex tw-justify-between tw-py-2">
          <div class="tw-text-gray-500">Credits Each Cycle</div>
          <div class="tw-space-x-4">
            <span v-for="item in plan.itemCategories" :key="item.businessItemCategoryId">{{ item.quantity }}</span>
          </div>
        </div>
        <div class="tw-flex tw-justify-between tw-py-2">
          <div class="tw-text-gray-500">Price</div>
          <div class="tw-flex tw-items-baseline">
            {{ formatCurrency(plan.priceCents, plan.currency) }} <span class="tw-text-gray-600 tw-text-xs tw-ml-1">+ applicable taxes</span>
          </div>
        </div>
        <div class="tw-flex tw-justify-between tw-py-2">
          <div class="tw-text-gray-500">Cycle</div>
          <div>{{ plan.cycleFrequency }} {{ plan.cycleInterval }}</div>
        </div>
        <div class="tw-flex tw-justify-between tw-py-2" v-if="plan.downPaymentCents > 0">
          <div class="tw-text-gray-500">Down Payment</div>
          <div class="tw-flex tw-items-baseline">
            {{ formatCurrency(plan.downPaymentCents, plan.currency) }} {{ plan.cycleInterval }} <span class="tw-text-gray-600 tw-text-xs tw-ml-1">+ applicable taxes</span>
          </div>
        </div>
      </base-card>
    </div>
    <base-card class="tw-mt-4">
      <CreditCard :user-id="userId" :user-business-plan-id="userBusinessPlanId" />
      <div v-if="showValidation" class="tw-text-red-500 tw-mt-4">{{ validationMessage }}</div>
    </base-card>
    <div class="tw-flex tw-justify-end tw-mt-4">
      <base-button class="tw-w-full sm:tw-w-auto" @click="retryActivatePlan" :loading="isReactivating" v-if="isRetryAvailable">Retry Activate Plan</base-button>
      <base-button class="tw-w-full sm:tw-w-auto" @click="submit" :loading="isActivating" v-else>Activate</base-button>
    </div>
  </base-content>
</template>

<script>
import CreditCard from "@/components/CreditCardv2/CreditCard";
import moment from "moment";
import { loadStripe } from "@stripe/stripe-js/pure";

export default {
  components: {
    CreditCard,
  },
  props: {
    userId: String,
    userBusinessPlanId: String,
  },
  data() {
    return {
      isLoading: false,
      isActivating: false,
      isReactivating: false,
      isRetryAvailable: false,
      showValidation: false,
      stripe: null,
      plan: {},
      hasCard: false,
      validationMessage: "",
      invoiceId: null,
      clientSecret: null,
      stripeConnectedAccountId: null,
    };
  },
  created() {
    this.getPlan();
  },
  methods: {
    async getPlan() {
      try {
        this.isLoading = true;
        const response = await this.$axios.get(`/users/${this.userId}/plans/${this.userBusinessPlanId}`);
        this.plan = response.data;
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(date) {
      return moment
        .utc(date, "YYYY-MM-DD")
        .local()
        .format("MM/DD/YYYY");
    },
    formatCurrency(value, currency) {
      return currency ? new Intl.NumberFormat(this.locale, { style: "currency", currency: currency }).format(value / 100) : "";
    },
    async submit() {
      try {
        this.isActivating = true;

        await this.checkPaymentMethod();
        if (!this.hasCard) {
          this.validationMessage = "Please enter a payment method to continue";
          this.showValidation = true;
          return;
        } else if (this.plan.status != "Awaiting Payment") {
          this.validationMessage = "This plan is not aligible for Initial Activation";
          this.showValidation = true;
          return;
        } else {
          this.showValidation = false;
        }
        await this.activatePlan();
        this.showValidation = false;
      } finally {
        this.isActivating = false;
      }
    },
    async checkPaymentMethod() {
      try {
        const response = await this.$axios.get(`/users/${this.userId}/creditcard`);
        if (response.data.last4) {
          this.hasCard = true;
        }
      } catch {
        this.hasCard = false;
      }
    },
    async activatePlan() {
      this.stripe = await loadStripe(this.$env.stripe_publishable_key, { stripeAccount: this.stripeConnectedAccountId });

      const response = await this.$axios.post(`/users/${this.userId}/plans/${this.userBusinessPlanId}/activate`, {
        businessId: this.plan.businessId,
      });
      this.clientSecret = response.data.clientSecret;
      this.stripeConnectedAccountId = response.data.stripeConnectedAccountId;
      this.handleResponse(response.data.subscriptionStatus, response.data.paymentStatus, response.data.clientSecret, response.data.invoiceId);
    },
    async handleResponse(subscriptionStatus, paymentStatus, clientSecret, invoiceId) {
      this.invoiceId = invoiceId;

      if (subscriptionStatus === "active" && paymentStatus === "succeeded") {
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Payment Succeeded", message: "Your plan has been activated" });
        this.$router.push({ path: `/users/${this.userId}/businesses/${this.plan.businessId}/plans/${this.userBusinessPlanId}/details` });
      } else if (subscriptionStatus === "active" && paymentStatus === "no payment status available") {
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Plan Waiting", message: "Your plan is active and will start on your specified start date." });
        this.$router.push({ path: `/users/${this.userId}/businesses/${this.plan.businessId}/plans/${this.userBusinessPlanId}/details` });
      } else if (subscriptionStatus === "incomplete" && paymentStatus === "requires_payment_method") {
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Payment failed", message: "Your payment failed" });
        this.validationMessage = "Your payment failed. Please update your payment method to continue.";
        this.isRetryAvailable = true;
        this.showValidation = true;
      } else if (subscriptionStatus === "incomplete" && paymentStatus === "requires_action") {
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Action Required", message: "Your bank requires additional information" });

        //Show Stripe SCA box
        this.stripe.confirmCardPayment(clientSecret).then(
          async function(result) {
            if (result.error) {
              this.validationMessage = result.error.message;
              this.showValidation = true;
            } else {
              await this.retryActivatePlan();
            }
          }.bind(this),
        );
      }
    },

    async retryActivatePlan() {
      try {
        this.isReactivating = true;

        const response = await this.$axios.post(`/users/${this.userId}/plans/${this.userBusinessPlanId}/activateretry`, {
          businessId: this.plan.businessId,
          invoiceId: this.invoiceId,
        });

        this.isLoading = false;

        if (response.data.paymentStatus == "paid") {
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Plan Activated", message: "Your plan has been activated" });
          this.$router.push({ path: `/users/${this.userId}/businesses/${this.plan.businessId}/plans/${this.userBusinessPlanId}/details` });
        } else if (response.data.paymentStatus == "open") {
          this.validationMessage = "There is an issue with your payment. Please update payment to continue.";
          this.showValidation = true;
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Payment failed", message: "There is an issue with your payment." });
        }
      } finally {
        this.isReactivating = false;
      }
    },
  },
};
</script>
